// Turn what a reviewer is looking at into an issue, without leaving the
// view. The point is the SNAPSHOT: a diff read in words loses the thing
// that made it worth raising, so the image of the model as it stands
// goes on the issue as its first attachment.
//
// Extracted rather than written inline because three surfaces want it -
// the model viewer opened with ?diff=, the Review Center's delta view,
// and the issue page's own 3D pane - and they must produce the same
// issue.
import { url, tokenRef } from "api";
import { CHANGE_LABELS } from "helpers/diffOverlay";
import { askEstimatedHours } from "components/Issue/HoursSpent";

const auth = () => {
  const t = localStorage.getItem(tokenRef);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// The three.js canvas keeps its pixels only for the frame just drawn
// (the renderer has no preserved drawing buffer), so a capture draws and
// reads in one go - the viewer's captureFrame does exactly that. There
// is deliberately NO fallback to a bare canvas read: without a draw in
// the same task that returns a blank picture, and an issue filed with an
// empty screenshot is worse than one filed with none.
export function captureViewerPng(viewer) {
  try {
    if (viewer && typeof viewer.captureFrame === "function") {
      return viewer.captureFrame("image/png") || "";
    }
    if (viewer && viewer.renderer && viewer.scene && viewer.camera) {
      viewer.renderer.render(viewer.scene, viewer.camera);
      return viewer.renderer.domElement.toDataURL("image/png");
    }
  } catch (e) { /* nothing to capture */ }
  return "";
}

function dataUrlToBlob(dataUrl) {
  const [head, b64] = String(dataUrl).split(",");
  const mime = (head.match(/data:([^;]+)/) || [, "image/png"])[1];
  const bin = atob(b64 || "");
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Blob([buf], { type: mime });
}

// A description a person can act on: what changed, in what, and where -
// so the issue still means something when it is read next month with
// the viewer closed.
export function describeDiff(diff, focus) {
  const s = (diff && diff.summary) || {};
  const parts = [];
  ["added", "removed", "modified"].forEach((k) => {
    if (s[k]) parts.push(`${s[k]} ${CHANGE_LABELS[k] || k}`);
  });
  const lines = [];
  lines.push(parts.length ? `This revision ${parts.join(", ")}.` : "Raised from the revision diff view.");
  if (focus && focus.length) {
    lines.push("");
    lines.push("In view:");
    focus.slice(0, 12).forEach((r) => {
      lines.push(`  ${CHANGE_LABELS[r.change] || r.change} - ${[r.category, r.family, r.type].filter(Boolean).join(" · ")}`);
    });
    if (focus.length > 12) lines.push(`  ...and ${focus.length - 12} more`);
  }
  lines.push("");
  lines.push("The screenshot is the model as it stood when this was raised.");
  return lines.join("\n");
}

// Creates the issue, attaches the snapshot, returns the new issue - or
// { cancelled: true } when the person cancels the estimate prompt (nothing
// is created). One click, no form, so the estimated hours every new issue
// needs (owner, 2026-10-16) are asked for here; the view is captured FIRST,
// before a modal covers it.
export async function createIssueFromDiff({
  projectId, viewer, diff, focus = [], title, ganttItemId = null,
  estimatedHours = null, ask = askEstimatedHours,
}) {
  const png = captureViewerPng(viewer);
  const estimate = estimatedHours != null ? estimatedHours : await ask({ title: "Estimated hours for this issue" });
  if (estimate == null) return { cancelled: true };
  const body = {
    title: title || `Revision review: ${(diff && diff.summary && diff.summary.modified) || 0} edited, `
      + `${(diff && diff.summary && diff.summary.added) || 0} added, `
      + `${(diff && diff.summary && diff.summary.removed) || 0} deleted`,
    description: describeDiff(diff, focus),
    projectId,
    sourceType: "3d",
    priority: "normal",
    estimatedHours: estimate,
    createdFrom: "web",
  };
  if (ganttItemId) body.ganttItemId = ganttItemId;

  const res = await fetch(`${url}/issue/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth() },
    body: JSON.stringify(body),
  });
  const created = await res.json();
  if (!res.ok) throw new Error(created.err || "Could not create the issue");
  const issue = created.data || created.issue || created;
  if (!issue || !issue._id) throw new Error("The issue came back without an id");

  if (png) {
    try {
      const fd = new FormData();
      fd.append("files", dataUrlToBlob(png), `diff-${Date.now()}.png`);
      await fetch(`${url}/issue/${issue._id}/attachments`, {
        method: "POST", headers: auth(), body: fd,
      });
    } catch (e) {
      // The issue exists; losing the snapshot is worth saying out loud
      // rather than failing the whole action.
      return { issue, href: issueHref(issue), snapshotFailed: true };
    }
  }
  return { issue, href: issueHref(issue), snapshotFailed: !png };
}

// WHERE to send someone for a freshly created issue. /issue/:x takes a
// SHARE TOKEN, not an id - opening /issue/<_id> made the page look the
// id up as a token and report "issue not found" for an issue that had
// been created perfectly well. The helper owns this so every caller
// agrees.
export function issueHref(issue) {
  if (!issue) return "/issues";
  return issue.shareToken
    ? `/issue/${issue.shareToken}/resolve`
    : `/issue-viewer/${issue._id}`;
}
